import React, { useEffect, useState } from 'react';
import { ScrollView, useWindowDimensions } from 'react-native';
import { DNABox, DNAButton, DNAModal, DNAText, Modal } from '@alucio/lux-ui';
import { Bulletin, BulletinStatus, User } from '@alucio/aws-beacon-amplify/src/models';
import InputComponent from 'src/components/Publishers/InputComponent';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useDispatch } from 'src/state/redux';
import { bulletinActions } from 'src/state/redux/slice/bulletin';
import { BulletinPost } from './BulletinPost';
import { BulletinRichText } from './BulletinRichText';
import {
  BULLETIN_CONTENT_MAX_LENGTH,
  BULLETIN_TITLE_MAX_LENGTH,
  EMPTY_BULLETIN_CONTENT,
  parseBulletinContent,
  validateBulletinInput,
} from './bulletin.utils';
import { saveBulletin } from './bulletin.service';

interface Props {
  bulletin?: Bulletin;
  user: User;
  onClose: () => void;
}

export const BulletinEditor: React.FC<Props> = ({ bulletin, user, onClose }) => {
  const { width, height } = useWindowDimensions();
  const { isOnline } = useAppSettings();
  const dispatch = useDispatch();
  const [title, setTitle] = useState(bulletin?.title || '');
  const [content, setContent] = useState(
    parseBulletinContent(bulletin?.content) || EMPTY_BULLETIN_CONTENT,
  );
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const input = { title, content };
  const isPublished = bulletin?.status === BulletinStatus.ACTIVE;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const validate = (requireContent = true) => {
    try {
      const valid = validateBulletinInput(input, requireContent);
      setError('');
      return valid;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Check the post before publishing.');
      return undefined;
    }
  };
  const save = async (asDraft = false) => {
    const valid = validate(!asDraft);
    if (!valid || busy) return;
    setBusy(true);
    try {
      const saved = asDraft
        ? await saveBulletin(valid, user, bulletin?.id, BulletinStatus.DRAFT)
        : await saveBulletin(valid, user, bulletin?.id);
      dispatch(bulletinActions.upsert(saved));
      onClose();
    } catch {
      setError('The post could not be saved. Your changes are still here. Please try again.');
      setBusy(false);
    }
  };

  return (
    <Modal
      visible
      backdropStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
      style={{
        width: Math.min(800, width - 32),
        height: Math.min(660, height - 48),
        maxHeight: 'calc(var(--beacon-viewport-height, 100dvh) - 48px)',
      }}
    >
      <DNAModal style={{ paddingVertical: 16 }}>
        <DNAModal.Header onClose={onClose} disabled={busy}>
          {preview ? 'Preview post' : bulletin ? 'Edit post' : 'Create post'}
        </DNAModal.Header>
        <ScrollView
          style={{ flex: 1, minHeight: 0 }}
          contentContainerStyle={{ padding: 16 }}
        >
          <DNABox appearance="col" spacing="md">
            {preview ? (
              <BulletinPost bulletin={{ ...input, publishedAt: bulletin?.publishedAt }} />
            ) : (
              <DNABox appearance="col" spacing="md">
                <DNABox appearance="col" spacing="xs">
                  <DNAText>Title *</DNAText>
                  <InputComponent
                    title="Title"
                    hideLabel
                    value={title}
                    onChangeText={setTitle}
                    characterLimit={BULLETIN_TITLE_MAX_LENGTH}
                    disabled={busy}
                    autoFocus
                    testID="bulletin-title"
                    removeMarginPadding
                  />
                </DNABox>
                <DNABox appearance="col" spacing="xs">
                  <DNAText>Content *</DNAText>
                  <BulletinRichText
                    value={content}
                    onChange={setContent}
                    editorHeight={280}
                    maxLength={BULLETIN_CONTENT_MAX_LENGTH}
                    disabled={busy}
                  />
                </DNABox>
              </DNABox>
            )}
            {!isOnline && (
              <DNAText status="tertiary">Changes will sync when you’re back online.</DNAText>
            )}
            {!!error && (
              <DNAText status="danger" accessibilityRole="alert" testID="bulletin-editor-error">
                {error}
              </DNAText>
            )}
          </DNABox>
        </ScrollView>
        <DNAModal.Confirmation>
          <DNAButton appearance="outline" status="tertiary" disabled={busy} onPress={onClose}>
            Cancel
          </DNAButton>
          <DNAButton
            appearance="outline"
            disabled={busy}
            onPress={() => {
              if (preview) setPreview(false);
              else if (validate()) setPreview(true);
            }}
          >
            {preview ? 'Back to editor' : 'Preview'}
          </DNAButton>
          {!isPublished && (
            <DNAButton appearance="outline" disabled={busy} onPress={() => save(true)} testID="bulletin-save-draft">
              Save draft
            </DNAButton>
          )}
          <DNAButton disabled={busy} onPress={() => save()} testID="bulletin-publish">
            {busy ? 'Saving…' : isPublished ? 'Save changes' : 'Publish'}
          </DNAButton>
        </DNAModal.Confirmation>
      </DNAModal>
    </Modal>
  );
};
